/**
 * SaveProgress use case (contracts.md): build SaveData from domain, call StoragePort.save.
 *
 * Deliberately synchronous: localStorage.setItem is synchronous, so data is persisted
 * before this function returns — eliminating the race condition where page reload / scene
 * transition happened before the Promise resolved.
 * StoragePort.save still returns Promise<SaveResult> for future async adapters (IndexedDB);
 * we fire-and-forget it here because the actual write already completed synchronously.
 */

import { serializeSession, serializeMapProgress, SAVE_VERSION } from "../../domain/save.js";
import type { StoragePort } from "../ports.js";
import type { MapProgress } from "../../domain/map-progress.js";
import type { MageProfile } from "../../domain/mage-profile.js";

export interface SaveProgressDeps {
  storage: StoragePort;
  getSession(): import("../../domain/session.js").GameSession | null;
  getMapProgress?: () => MapProgress | undefined;
  getMageProfile?: () => MageProfile | undefined;
}

export function createSaveProgress(deps: SaveProgressDeps) {
  return function saveProgress(): void {
    const session = deps.getSession();
    const mp = deps.getMapProgress?.();
    const mageProfile = deps.getMageProfile?.();
    const data = {
      version: SAVE_VERSION,
      currentSession: session ? serializeSession(session) : undefined,
      mapProgress: mp ? serializeMapProgress(mp) : undefined,
      mageProfile: mageProfile ?? undefined,
    };
    // localStorage.setItem is synchronous; the Promise wrapper is fire-and-forget.
    void deps.storage.save(data);
  };
}
